import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  TAREAS_HOUSEKEEPING,
  RESUMEN_HOUSEKEEPING,
  CREAR_TAREA_HOUSEKEEPING,
  ACTUALIZAR_TAREA_HOUSEKEEPING,
  ASIGNAR_TAREA_HOUSEKEEPING,
} from '../../graphql/housekeeping';
import { GET_HABITACIONES } from '../../graphql/habitaciones';
import { GET_USUARIOS } from '../../graphql/configuracion';
import { Plus, Search, Clock, Play, CheckCircle, AlertTriangle, BedDouble } from 'lucide-react';
import './TableroTareas.css';

const ESTADOS_FILTRO = [
  { value: '', label: 'Tareas pendientes de completar' },
  { value: 'pendiente', label: 'Pendientes' },
  { value: 'en_progreso', label: 'En Progreso' },
  { value: 'completada', label: 'Completadas' },
  { value: 'cancelada', label: 'Canceladas' },
];

const PRIORIDAD_COLOR = {
  urgente: '#ef4444',
  alta: '#f59e0b',
  normal: '#22c55e',
  baja: '#94a3b8',
};

const TIPO_LABEL = {
  limpieza: 'Limpieza',
  mantenimiento: 'Mantenimiento',
  inspeccion: 'Inspección',
  limpieza_profunda: 'Limpieza Profunda',
};

function TableroTareas() {
  const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
  const [filtroEstado, setFiltroEstado] = useState('');
  const [fechaDesde, setFechaDesde] = useState(hoy);
  const [fechaHasta, setFechaHasta] = useState(hoy);
  const [busqueda, setBusqueda] = useState('');
  const [showModal, setShowModal] = useState(false);

  // Form state for new task
  const [nuevaTarea, setNuevaTarea] = useState({
    habitacion_id: '',
    tipo: 'limpieza',
    prioridad: 'normal',
    usuario_asignado_id: '',
    notas: '',
    fecha: hoy,
  });

  const variables = { fecha: fechaDesde };
  if (filtroEstado) variables.estado = filtroEstado;

  const { data, loading } = useQuery(TAREAS_HOUSEKEEPING, {
    variables,
    pollInterval: 10000,
  });

  const { data: resumenData } = useQuery(RESUMEN_HOUSEKEEPING, {
    variables: { fecha: fechaDesde },
    pollInterval: 10000,
  });

  const { data: habData } = useQuery(GET_HABITACIONES);
  const { data: usersData } = useQuery(GET_USUARIOS);

  const [crearTarea] = useMutation(CREAR_TAREA_HOUSEKEEPING, {
    refetchQueries: [{ query: TAREAS_HOUSEKEEPING, variables }, { query: RESUMEN_HOUSEKEEPING, variables: { fecha: fechaDesde } }],
  });

  const [actualizarTarea] = useMutation(ACTUALIZAR_TAREA_HOUSEKEEPING, {
    refetchQueries: [{ query: TAREAS_HOUSEKEEPING, variables }, { query: RESUMEN_HOUSEKEEPING, variables: { fecha: fechaDesde } }],
  });

  const [asignarTarea] = useMutation(ASIGNAR_TAREA_HOUSEKEEPING, {
    refetchQueries: [{ query: TAREAS_HOUSEKEEPING, variables }],
  });

  const tareas = data?.tareasHousekeeping || [];
  const resumen = resumenData?.resumenHousekeeping;
  const habitaciones = habData?.habitaciones || [];
  const usuarios = (usersData?.usuarios || []).filter(u => u.activo);
  const staffLimpieza = usuarios.filter(u => ['limpieza', 'mantenimiento'].includes(u.rol));

  // Filter by search
  const tareasFiltradas = tareas.filter(t => {
    if (!busqueda) return true;
    const texto = busqueda.toLowerCase();
    return (
      t.habitacion?.numero?.toLowerCase().includes(texto) ||
      t.usuario_asignado?.nombre?.toLowerCase().includes(texto) ||
      t.tipo?.toLowerCase().includes(texto) ||
      t.notas?.toLowerCase().includes(texto)
    );
  });

  const handleCrearTarea = async (e) => {
    e.preventDefault();
    try {
      await crearTarea({
        variables: {
          input: {
            habitacion_id: parseInt(nuevaTarea.habitacion_id),
            tipo: nuevaTarea.tipo,
            prioridad: nuevaTarea.prioridad,
            usuario_asignado_id: nuevaTarea.usuario_asignado_id ? parseInt(nuevaTarea.usuario_asignado_id) : null,
            notas: nuevaTarea.notas || null,
            fecha: nuevaTarea.fecha,
          },
        },
      });
      setShowModal(false);
      setNuevaTarea({ habitacion_id: '', tipo: 'limpieza', prioridad: 'normal', usuario_asignado_id: '', notas: '', fecha: hoy });
    } catch (err) {
      console.error('Error creando tarea:', err);
      alert('Error al crear tarea: ' + err.message);
    }
  };

  const handleActualizar = async (id, estado) => {
    try {
      await actualizarTarea({ variables: { id: parseInt(id), input: { estado } } });
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleAsignar = async (tareaId, usuarioId) => {
    try {
      await asignarTarea({ variables: { id: parseInt(tareaId), usuario_id: parseInt(usuarioId) } });
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  return (
    <div className="tt">
      <div className="tt__header">
        <h2 className="tt__title">Tablero de tareas</h2>
        <button className="tt__btn-nueva" onClick={() => setShowModal(true)}>
          <Plus size={16} /> Programar nueva tarea
        </button>
      </div>

      {/* Filters */}
      <div className="tt__filtros">
        <select
          className="tt__filtro-select"
          value={filtroEstado}
          onChange={e => setFiltroEstado(e.target.value)}
        >
          {ESTADOS_FILTRO.map(e => (
            <option key={e.value} value={e.value}>{e.label}</option>
          ))}
        </select>
        <input
          type="date"
          className="tt__filtro-input"
          value={fechaDesde}
          onChange={e => setFechaDesde(e.target.value)}
          placeholder="Programadas desde"
        />
        <input
          type="date"
          className="tt__filtro-input"
          value={fechaHasta}
          onChange={e => setFechaHasta(e.target.value)}
          placeholder="Programadas hasta"
        />
        <div className="tt__busqueda">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar por nombre de la tarea"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </div>
      </div>

      {/* Summary stats */}
      {resumen && (
        <div className="tt__resumen">
          <div className="tt__resumen-item tt__resumen-item--pendiente">
            <Clock size={14} /> <span>{resumen.pendientes}</span> Pendientes
          </div>
          <div className="tt__resumen-item tt__resumen-item--progreso">
            <Play size={14} /> <span>{resumen.en_progreso}</span> En Progreso
          </div>
          <div className="tt__resumen-item tt__resumen-item--completada">
            <CheckCircle size={14} /> <span>{resumen.completadas}</span> Completadas
          </div>
          <div className="tt__resumen-item tt__resumen-item--limpieza">
            <AlertTriangle size={14} /> <span>{resumen.habitaciones_en_limpieza}</span> Hab. Limpieza
          </div>
        </div>
      )}

      {/* Task table */}
      <div className="tt__tabla-wrapper">
        {loading ? (
          <div className="tt__loading">Cargando tareas...</div>
        ) : tareasFiltradas.length === 0 ? (
          <div className="tt__empty">No hay tareas para los filtros seleccionados</div>
        ) : (
          <table className="tt__tabla">
            <thead>
              <tr>
                <th></th>
                <th>Tarea</th>
                <th>Responsables</th>
                <th>Fecha máxima</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {tareasFiltradas.map(tarea => (
                <tr key={tarea.id} className="tt__fila">
                  <td>
                    <div
                      className="tt__prioridad-bar"
                      style={{ backgroundColor: PRIORIDAD_COLOR[tarea.prioridad] || '#94a3b8' }}
                      title={tarea.prioridad}
                    />
                  </td>
                  <td>
                    <div className="tt__tarea-info">
                      <BedDouble size={14} />
                      <span className="tt__tarea-hab">{tarea.habitacion?.numero || 'N/A'}</span>
                      <span className="tt__tarea-tipo">{TIPO_LABEL[tarea.tipo] || tarea.tipo}</span>
                      {tarea.notas && <span className="tt__tarea-notas" title={tarea.notas}>📝</span>}
                    </div>
                  </td>
                  <td>
                    {tarea.usuario_asignado ? (
                      <span className="tt__responsable">{tarea.usuario_asignado.nombre}</span>
                    ) : (
                      <select
                        className="tt__asignar-select"
                        defaultValue=""
                        onChange={e => e.target.value && handleAsignar(tarea.id, e.target.value)}
                      >
                        <option value="" disabled>Asignar</option>
                        {staffLimpieza.map(u => (
                          <option key={u.id} value={u.id}>{u.nombre}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    <span className="tt__fecha">
                      {(() => {
                        if (!tarea.fecha) return '—';
                        // Handle "YYYY-MM-DD" strings: split and construct to avoid timezone issues
                        const parts = tarea.fecha.split('T')[0].split('-');
                        if (parts.length === 3) {
                          const d = new Date(parts[0], parts[1] - 1, parts[2]);
                          return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-CO');
                        }
                        const d = new Date(tarea.fecha);
                        return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-CO');
                      })()}
                    </span>
                  </td>
                  <td>
                    <div className="tt__acciones">
                      {tarea.estado === 'pendiente' && (
                        <button className="tt__btn tt__btn--iniciar" onClick={() => handleActualizar(tarea.id, 'en_progreso')}>
                          Iniciar
                        </button>
                      )}
                      {(tarea.estado === 'pendiente' || tarea.estado === 'en_progreso') && (
                        <button className="tt__btn tt__btn--completar" onClick={() => handleActualizar(tarea.id, 'completada')}>
                          Completar
                        </button>
                      )}
                      {tarea.estado === 'completada' && (
                        <span className="tt__estado-badge tt__estado-badge--completada">Completada</span>
                      )}
                      {tarea.estado === 'cancelada' && (
                        <span className="tt__estado-badge tt__estado-badge--cancelada">Cancelada</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* New task modal */}
      {showModal && (
        <div className="tt__modal-overlay" onClick={() => setShowModal(false)}>
          <div className="tt__modal" onClick={e => e.stopPropagation()}>
            <h3>Programar nueva tarea</h3>
            <form onSubmit={handleCrearTarea}>
              <div className="tt__modal-field">
                <label>Habitación *</label>
                <select required value={nuevaTarea.habitacion_id} onChange={e => setNuevaTarea({ ...nuevaTarea, habitacion_id: e.target.value })}>
                  <option value="">Seleccionar habitación</option>
                  {habitaciones.map(h => (
                    <option key={h.id} value={h.id}>{h.numero} - {h.tipo}</option>
                  ))}
                </select>
              </div>
              <div className="tt__modal-field">
                <label>Tipo</label>
                <select value={nuevaTarea.tipo} onChange={e => setNuevaTarea({ ...nuevaTarea, tipo: e.target.value })}>
                  <option value="limpieza">Limpieza</option>
                  <option value="mantenimiento">Mantenimiento</option>
                  <option value="inspeccion">Inspección</option>
                  <option value="limpieza_profunda">Limpieza Profunda</option>
                </select>
              </div>
              <div className="tt__modal-field">
                <label>Prioridad</label>
                <select value={nuevaTarea.prioridad} onChange={e => setNuevaTarea({ ...nuevaTarea, prioridad: e.target.value })}>
                  <option value="baja">Baja</option>
                  <option value="normal">Normal</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
              <div className="tt__modal-field">
                <label>Asignar a</label>
                <select value={nuevaTarea.usuario_asignado_id} onChange={e => setNuevaTarea({ ...nuevaTarea, usuario_asignado_id: e.target.value })}>
                  <option value="">Sin asignar</option>
                  {staffLimpieza.map(u => (
                    <option key={u.id} value={u.id}>{u.nombre}</option>
                  ))}
                </select>
              </div>
              <div className="tt__modal-field">
                <label>Fecha</label>
                <input type="date" value={nuevaTarea.fecha} onChange={e => setNuevaTarea({ ...nuevaTarea, fecha: e.target.value })} />
              </div>
              <div className="tt__modal-field">
                <label>Notas</label>
                <textarea rows={3} value={nuevaTarea.notas} onChange={e => setNuevaTarea({ ...nuevaTarea, notas: e.target.value })} />
              </div>
              <div className="tt__modal-actions">
                <button type="button" className="tt__btn tt__btn--cancelar" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="tt__btn tt__btn--crear">Crear Tarea</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TableroTareas;
